import type { Question, QuestionOption, QuizBank, HQBFile } from '../types';

export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export function shuffleQuestions(questions: Question[]): Question[] {
  return shuffleArray(questions);
}

export function shuffleOptions(question: Question): Question {
  if (question.type === 'judge') return question;
  const optionPairs: [string, string][] = question.options.map((o) => [o.label, o.content]);
  const shuffledPairs = shuffleArray(optionPairs);
  const labels = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const newOptions: QuestionOption[] = shuffledPairs.map(([_, content], i) => ({
    label: labels[i],
    content,
  }));
  const labelMap = new Map<string, string>();
  shuffledPairs.forEach(([oldLabel], i) => {
    labelMap.set(oldLabel, labels[i]);
  });
  const newAnswer = question.answer.map((a) => labelMap.get(a) || a);
  return { ...question, options: newOptions, answer: newAnswer };
}

export function parseHQB(json: string): HQBFile | null {
  try {
    const data = JSON.parse(json);
    if (!data.hqbVersion || !data.metadata || !data.questions) {
      return null;
    }
    return data as HQBFile;
  } catch {
    return null;
  }
}

export function validateHQB(data: unknown): data is HQBFile {
  if (typeof data !== 'object' || data === null) return false;
  const obj = data as Record<string, unknown>;
  if (!obj.hqbVersion || !obj.metadata || !Array.isArray(obj.questions)) return false;
  return true;
}

export function generateBankId(_name: string): string {
  return 'bank_' + Date.now() + '_' + Math.random().toString(36).substring(2, 8);
}

export function checkAnswer(question: Question, selected: string[]): boolean {
  if (selected.length !== question.answer.length) return false;
  // Normalize answers: if answer stores content text instead of label, map to label
  const normalize = (val: string): string => {
    const opt = question.options.find(o => o.label === val);
    if (opt) return val; // already a label
    const byContent = question.options.find(o => o.content === val);
    return byContent ? byContent.label : val;
  };
  const sortedSelected = [...selected].map(normalize).sort();
  const sortedAnswer = [...question.answer].map(normalize).sort();
  return sortedSelected.every((v, i) => v === sortedAnswer[i]);
}

export function isAnswerCorrect(question: Question, selected: string[]): boolean {
  return checkAnswer(question, selected);
}

export function getAllQuestionsFromBank(bank: QuizBank): Question[] {
  return bank.hqb.questions;
}

export function getQuestionsByGroup(bank: QuizBank, groupId: string): Question[] {
  const group = bank.hqb.groups.find((g) => g.id === groupId);
  if (!group) return [];
  return group.questionIds
    .map((id) => bank.hqb.questions.find((q) => q.id === id))
    .filter(Boolean) as Question[];
}

export function searchQuestions(
  banks: QuizBank[],
  keyword: string,
  bankIds?: string[]
): Array<{ bank: QuizBank; question: Question }> {
  const normalizedKeyword = keyword.toLowerCase().trim();
  const targetBanks = bankIds && bankIds.length > 0
    ? banks.filter((b) => bankIds.includes(b.id))
    : banks;

  const results: Array<{ bank: QuizBank; question: Question }> = [];
  targetBanks.forEach((bank) => {
    bank.hqb.questions.forEach((question) => {
      const inContent = question.content.toLowerCase().includes(normalizedKeyword);
      const inOptions = question.options.some((o) =>
        o.content.toLowerCase().includes(normalizedKeyword)
      );
      if (inContent || inOptions) {
        results.push({ bank, question });
      }
    });
  });
  return results;
}

export function getStats(bank: QuizBank): {
  total: number;
  single: number;
  multiple: number;
  judge: number;
  groups: number;
} {
  return {
    total: bank.hqb.questions.length,
    single: bank.hqb.questions.filter((q) => q.type === 'single').length,
    multiple: bank.hqb.questions.filter((q) => q.type === 'multiple').length,
    judge: bank.hqb.questions.filter((q) => q.type === 'judge').length,
    groups: bank.hqb.groups.length,
  };
}

const STORAGE_KEY_PREFIX = 'hodquiz_';
const COOKIE_PREFIX = 'hq_';

// Cookie helpers for settings persistence
export function saveCookie(key: string, value: string, days = 365): void {
  try {
    const d = new Date();
    d.setTime(d.getTime() + days * 86400000);
    document.cookie = `${COOKIE_PREFIX}${key}=${encodeURIComponent(value)};path=/;expires=${d.toUTCString()};SameSite=Lax`;
  } catch {
    // ignore
  }
}

export function loadCookie(key: string): string | null {
  try {
    const prefix = `${COOKIE_PREFIX}${key}=`;
    const parts = document.cookie.split(';');
    for (let part of parts) {
      part = part.trimStart();
      if (part.startsWith(prefix)) {
        return decodeURIComponent(part.slice(prefix.length));
      }
    }
    return null;
  } catch {
    return null;
  }
}

export function saveToStorage<T>(key: string, value: T): void {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + key, JSON.stringify(value));
  } catch {
    // Storage full or unavailable
  }
}

export function loadFromStorage<T>(key: string, defaultValue: T): T {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + key);
    if (raw === null) return defaultValue;
    return JSON.parse(raw) as T;
  } catch {
    return defaultValue;
  }
}

export function removeFromStorage(key: string): void {
  try {
    localStorage.removeItem(STORAGE_KEY_PREFIX + key);
  } catch {
    // ignore
  }
}

export function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
